/**
 * Los encargos que trae esta factura, antes de aplicarla.
 *
 * Viene propuesto (los más viejos primero, hasta las unidades de cada línea)
 * y se confirma o se cambia: una unidad que en realidad es para stock se
 * destilda, y entra al stock como cualquier otra.
 */
import { PackageCheck } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { type FilaCompra } from "@/components/compras/logica";
import { fechaCorta } from "@/lib/periodo";
import { type Encargo } from "@/lib/tipos";

import { alternar, filasConEncargos, resumenDeFila, type SeleccionEncargos } from "./logica";

export interface EncargosDeLaCompraProps {
  filas: ReadonlyArray<FilaCompra>;
  encargos: ReadonlyArray<Encargo>;
  seleccion: SeleccionEncargos;
  onSeleccion: (seleccion: SeleccionEncargos) => void;
}

export function EncargosDeLaCompra({
  filas,
  encargos,
  seleccion,
  onSeleccion,
}: EncargosDeLaCompraProps): JSX.Element | null {
  const grupos = filasConEncargos(filas, encargos);
  if (grupos.length === 0) return null;

  return (
    <Card padding="normal" className="space-y-4">
      <div className="flex items-start gap-3">
        <PackageCheck className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium">Esta factura trae encargos</p>
          <p className="text-xs text-muted-foreground">
            Lo tildado se da por recibido con el costo de la factura y no se suma al stock: ya es
            del cliente. Destildá lo que sea para stock.
          </p>
        </div>
      </div>

      {grupos.map(({ fila, encargos: delProducto }) => {
        const tildados = seleccion[fila.id] ?? [];
        return (
          <div key={fila.id} className="space-y-2 border-t pt-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-medium">{fila.descripcion}</p>
              <p className="text-xs text-muted-foreground">
                {resumenDeFila(fila, tildados.length)}
              </p>
            </div>
            <div className="space-y-1">
              {delProducto.map((e) => (
                <Checkbox
                  key={e.id}
                  checked={tildados.includes(e.id)}
                  onChange={() => onSeleccion(alternar(seleccion, fila, e.id))}
                  label={
                    <span>
                      Encargo de {e.cliente ?? "consumidor final"} · {e.venta} ·{" "}
                      {fechaCorta(e.fecha_venta)}
                    </span>
                  }
                />
              ))}
            </div>
          </div>
        );
      })}
    </Card>
  );
}
